import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Ban, CheckCircle2, Play, Plus, RotateCcw } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { formatDate, formatDateTime } from "../lib/format";
import { isManagerOrAbove } from "../lib/people";
import type { Employee, Task, TaskWrite } from "../api/types";

const STATUS_TONE: Record<string, "emerald" | "sky" | "slate" | "rose" | "amber"> = {
  pending: "amber",
  in_progress: "sky",
  done: "emerald",
  cancelled: "slate",
};

const PRIORITY_TONE: Record<string, "rose" | "slate" | "sky"> = {
  high: "rose",
  normal: "slate",
  low: "sky",
};

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
] as const;

/**
 * Daily tasks: the management board the audit tagged as missing (TSK-008).
 *
 * The API scopes reads by role -- an owner sees every task, a manager sees
 * their reporting subtree, an employee only their own -- so this one screen is
 * the team board and the personal one at once. Creating, editing, cancelling
 * and reopening are management decisions, enforced server-side; an employee
 * reads their list and advances their own task from Start to Complete.
 */
export function TasksPage() {
  const { user } = useAuth();
  const isManager = isManagerOrAbove(user?.role);
  const timezone = user?.account?.timezone;
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const status = params.get("status") ?? "all";
  const priority = params.get("priority") ?? "";
  const assignee = params.get("assignee") ?? "";

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);

  // "open" and "all" both mean "do not filter server-side"; "open" is then
  // narrowed in the browser so one request still covers Not Started + In
  // progress, which the exact-match filter cannot express.
  const serverStatus = status === "open" || status === "all" ? undefined : status;

  const tasks = useQuery({
    queryKey: ["tasks", status, priority, assignee],
    queryFn: ({ signal }) =>
      api.tasksPage(
        {
          status: serverStatus,
          priority: priority || undefined,
          assignee_employee: assignee || undefined,
          page_size: 50,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    enabled: isManager,
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(() => {
    const all = tasks.data?.results ?? [];
    const filtered =
      status === "open"
        ? all.filter((task) => task.status === "pending" || task.status === "in_progress")
        : all;
    // Active work sits above the history, and a task that was just completed
    // settles below the open ones rather than vanishing from the board.
    const rank: Record<string, number> = { pending: 0, in_progress: 1, done: 2, cancelled: 3 };
    return [...filtered].sort((a, b) => {
      const byStatus = (rank[a.status ?? "pending"] ?? 9) - (rank[b.status ?? "pending"] ?? 9);
      if (byStatus !== 0) return byStatus;
      return (a.due_at ?? "9999-12-31").localeCompare(b.due_at ?? "9999-12-31");
    });
  }, [tasks.data, status]);

  const openCount = rows.filter(
    (task) => task.status === "pending" || task.status === "in_progress",
  ).length;

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  // The desks read their own "my tasks" cache; refresh it so a decision here
  // is reflected the moment the employee returns to their desk.
  const invalidateAll = () => {
    invalidate();
    void queryClient.invalidateQueries({ queryKey: ["manager-desk-tasks"] });
  };

  const act = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: "start" | "complete" | "reopen" | "cancel";
    }) => {
      if (action === "start") return api.startTask(id);
      if (action === "complete") return api.completeTask(id);
      if (action === "reopen") return api.reopenTask(id);
      return api.cancelTask(id);
    },
    onSuccess: invalidateAll,
  });

  const create = useMutation({
    mutationFn: (body: TaskWrite) => api.createTask(body),
    onSuccess: () => {
      setCreating(false);
      invalidateAll();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<TaskWrite> }) =>
      api.updateTask(id, body),
    onSuccess: () => {
      setEditing(null);
      invalidateAll();
    },
  });

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle={
          isManager
            ? "Assign work, watch it move, and close it out."
            : "What is assigned to you, and where it stands."
        }
        actions={
          isManager ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus size={15} /> New task
            </Button>
          ) : undefined
        }
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <Filter label="Status">
            <select
              value={status}
              onChange={(event) => setFilter("status", event.target.value)}
              className={SELECT}
              aria-label="Status"
            >
              <option value="open">Open</option>
              <option value="pending">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="done">Done</option>
              <option value="cancelled">Cancelled</option>
              <option value="all">All</option>
            </select>
          </Filter>
          <Filter label="Priority">
            <select
              value={priority}
              onChange={(event) => setFilter("priority", event.target.value)}
              className={SELECT}
              aria-label="Priority"
            >
              <option value="">Any</option>
              {PRIORITIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Filter>
          {isManager ? (
            <Filter label="Assignee">
              <select
                value={assignee}
                onChange={(event) => setFilter("assignee", event.target.value)}
                className={SELECT}
                aria-label="Assignee"
              >
                <option value="">Anyone</option>
                {(employees.data ?? []).map((person: Employee) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
              </select>
            </Filter>
          ) : null}
          <span className="ml-auto text-sm text-slate-500">
            {openCount} open
          </span>
        </div>
      </Card>

      {tasks.isPending ? (
        <TableSkeleton rows={5} />
      ) : tasks.isError ? (
        <EmptyState
          title="Could not load tasks"
          body={tasks.error instanceof ApiError ? tasks.error.message : "Try again in a moment."}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No tasks"
          body={
            isManager
              ? "Assign a task to a team member and it will appear here."
              : "Tasks assigned to you will appear here."
          }
          action={
            isManager ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Plus size={15} /> New task
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-slate-100">
            {rows.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                isManager={isManager}
                timezone={timezone}
                busy={act.isPending}
                onAction={(action) => act.mutate({ id: task.id, action })}
                onEdit={() => setEditing(task)}
              />
            ))}
          </ul>
        </Card>
      )}

      {act.error instanceof ApiError ? (
        <p role="alert" className="mt-3 text-sm text-rose-600">
          {act.error.message}
        </p>
      ) : null}

      {creating ? (
        <Modal title="New task" onClose={() => setCreating(false)} wide>
          <TaskForm
            employees={employees.data ?? []}
            saving={create.isPending}
            error={create.error}
            onCancel={() => setCreating(false)}
            onSave={(body) => create.mutate(body)}
          />
        </Modal>
      ) : null}

      {editing ? (
        <Modal title="Edit task" onClose={() => setEditing(null)} wide>
          <TaskForm
            initial={editing}
            employees={employees.data ?? []}
            saving={update.isPending}
            error={update.error}
            onCancel={() => setEditing(null)}
            onSave={(body) => update.mutate({ id: editing.id, body })}
          />
        </Modal>
      ) : null}
    </>
  );
}

const SELECT =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex w-40 flex-col gap-1 text-xs font-medium text-slate-600">
      {label}
      {children}
    </label>
  );
}

function TaskRow({
  task,
  isManager,
  timezone,
  busy,
  onAction,
  onEdit,
}: {
  task: Task;
  isManager: boolean;
  timezone?: string | null | undefined;
  busy: boolean;
  onAction: (action: "start" | "complete" | "reopen" | "cancel") => void;
  onEdit: () => void;
}) {
  const status = task.status ?? "pending";
  const open = status === "pending" || status === "in_progress";
  const overdue = task.is_overdue;

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-900">
          {task.subject || "Untitled task"}
        </p>
        {task.body ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{task.body}</p>
        ) : null}
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          <span>{task.assignee_name || "Unassigned"}</span>
          <span aria-hidden>·</span>
          {task.due_at ? (
            <span className={overdue ? "font-medium text-rose-600" : undefined}>
              Due {formatDate(task.due_at, timezone ?? undefined)}
              {overdue ? " · overdue" : ""}
            </span>
          ) : (
            <span>No date</span>
          )}
          {task.created_by_name ? (
            <>
              <span aria-hidden>·</span>
              <span>by {task.created_by_name}</span>
            </>
          ) : null}
          <span aria-hidden>·</span>
          <Badge tone={PRIORITY_TONE[task.priority ?? "normal"] ?? "slate"}>
            {task.priority ?? "normal"}
          </Badge>
          {task.completed_at ? (
            <>
              <span aria-hidden>·</span>
              <span>Completed {formatDateTime(task.completed_at, timezone ?? undefined)}</span>
            </>
          ) : null}
        </p>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <Badge tone={STATUS_TONE[status] ?? "slate"}>
          {task.status_display ?? status}
        </Badge>
        {open ? (
          <>
            {status === "pending" ? (
              <Button disabled={busy} onClick={() => onAction("start")}>
                <Play size={13} /> Start
              </Button>
            ) : null}
            <Button
              variant={status === "in_progress" ? "primary" : "secondary"}
              disabled={busy}
              onClick={() => onAction("complete")}
            >
              <CheckCircle2 size={13} /> Complete
            </Button>
          </>
        ) : isManager ? (
          <Button disabled={busy} onClick={() => onAction("reopen")}>
            <RotateCcw size={13} /> Reopen
          </Button>
        ) : null}
        {isManager ? (
          <>
            <Button disabled={busy} onClick={onEdit}>
              Edit
            </Button>
            {open ? (
              <Button variant="ghost" disabled={busy} onClick={() => onAction("cancel")}>
                <Ban size={13} /> Cancel
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </li>
  );
}

function TaskForm({
  initial,
  employees,
  saving,
  error,
  onSave,
  onCancel,
}: {
  initial?: Task | null;
  employees: Employee[];
  saving: boolean;
  error: unknown;
  onSave: (body: TaskWrite) => void;
  onCancel: () => void;
}) {
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [assignee, setAssignee] = useState(initial?.assignee_employee ?? "");
  const [priority, setPriority] = useState<string>(initial?.priority ?? "normal");
  const [dueAt, setDueAt] = useState(initial?.due_at ?? "");

  const apiError = error instanceof ApiError ? error : null;
  const fieldErrors = apiError?.fields ?? {};

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    onSave({
      subject: subject || undefined,
      body: body || undefined,
      assignee_employee: assignee || undefined,
      priority: priority as TaskWrite["priority"],
      due_at: dueAt || null,
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div>
        <label htmlFor="task-subject" className="block text-xs font-medium text-slate-600">
          Subject
        </label>
        <input
          id="task-subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          placeholder="What needs doing"
          className={INPUT}
        />
        {fieldErrors.subject ? (
          <p className="mt-1 text-xs text-rose-600">{fieldErrors.subject.join(" ")}</p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="task-assignee" className="block text-xs font-medium text-slate-600">
            Assignee
          </label>
          <select
            id="task-assignee"
            value={assignee}
            onChange={(event) => setAssignee(event.target.value)}
            className={SELECT}
          >
            <option value="">Choose an employee…</option>
            {employees.map((person) => (
              <option key={person.id} value={person.id}>
                {person.display_name}
              </option>
            ))}
          </select>
          {fieldErrors.assignee_employee ? (
            <p className="mt-1 text-xs text-rose-600">
              {fieldErrors.assignee_employee.join(" ")}
            </p>
          ) : null}
        </div>
        <div>
          <label htmlFor="task-due" className="block text-xs font-medium text-slate-600">
            Due
          </label>
          <input
            id="task-due"
            type="date"
            value={dueAt}
            onChange={(event) => setDueAt(event.target.value)}
            className={SELECT}
          />
          {fieldErrors.due_at ? (
            <p className="mt-1 text-xs text-rose-600">{fieldErrors.due_at.join(" ")}</p>
          ) : null}
        </div>
      </div>

      <div>
        <label htmlFor="task-priority" className="block text-xs font-medium text-slate-600">
          Priority
        </label>
        <select
          id="task-priority"
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
          className={SELECT}
        >
          {PRIORITIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="task-body" className="block text-xs font-medium text-slate-600">
          Detail
        </label>
        <textarea
          id="task-body"
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          className={INPUT}
        />
        {fieldErrors.body ? (
          <p className="mt-1 text-xs text-rose-600">{fieldErrors.body.join(" ")}</p>
        ) : null}
      </div>

      {apiError && !Object.keys(fieldErrors).length ? (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {apiError.message}
        </p>
      ) : null}

      <div className="flex items-center gap-2 pt-1">
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? "Saving…" : initial ? "Save task" : "Create task"}
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Discard
        </Button>
      </div>
    </form>
  );
}

const INPUT =
  "mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";
